import { Directive, HostListener, inject, input } from '@angular/core';
import { AuthSessionStatus } from '../auth/auth-core/models/auth-session.model';
import { AuthStateService } from '../auth/auth-core/services/auth-state.service';
import { ImpersonationService } from '../services/impersonation.service';

/** Session states where sign-out is already underway and the native "leave site?" prompt must not appear. */
const SIGN_OUT_STATUSES: ReadonlySet<AuthSessionStatus> = new Set([
  AuthSessionStatus.SigningOut,
  AuthSessionStatus.SignedOut,
  AuthSessionStatus.Expired,
]);

/**
 * UnsavedChangesDirective
 *
 * Prevents accidental page close/reload when there are unsaved changes. Shows browser's native confirmation dialog when
 * user attempts to leave the page.
 *
 * This directive listens to the `beforeunload` event and triggers the browser's native confirmation dialog if the
 * component has unsaved changes.
 *
 * @example
 *   ```html
 *   <div appUnsavedChanges [hasUnsavedChanges]="hasChanges()">
 *     <form>
 *       <!-- Your form content -->
 *     </form>
 *   </div>
 *   ```;
 *
 * @example
 *   With signal
 *   ```typescript
 *   export class MyComponent {
 *   readonly hasChanges = signal<boolean>(false);
 *
 *   onFormChange(): void {
 *   this.hasChanges.set(true);
 *   }
 *
 *   onFormSubmit(): void {
 *   this.hasChanges.set(false);
 *   }
 *   }
 *   ```
 *
 * @example
 *   In template
 *   ```html
 *   <div appUnsavedChanges [hasUnsavedChanges]="hasChanges()">
 *   <input (input)="onFormChange()" />
 *   <button (click)="onFormSubmit()">Submit</button>
 *   </div>
 *   ```
 *
 * @note Modern browsers show their own generic message and ignore custom text.
 * The browser will typically show something like: "Leave site? Changes you made may not be saved."
 *
 * @publicApi
 */
@Directive({
  selector: '[appUnsavedChanges]',
  standalone: true,
})
export class UnsavedChangesDirective {
  /**
   * Signal or boolean indicating whether there are unsaved changes. When true, the directive will prevent accidental
   * page navigation.
   */
  readonly hasUnsavedChanges = input<boolean>(false);

  /** Injected ImpersonationService to detect if the impersonation session is being ended. */
  private readonly impersonationService = inject(ImpersonationService, { optional: true });

  /** Injected AuthStateService to detect if an idle-timeout/manual sign-out is already in progress. */
  private readonly authState = inject(AuthStateService, { optional: true });

  /**
   * Handles the beforeunload event to prevent accidental page close/reload.
   *
   * When the user attempts to leave the page (refresh, close tab, navigate away), this handler checks if there are
   * unsaved changes and triggers the browser's native confirmation dialog if needed.
   *
   * @param event - The BeforeUnloadEvent from the browser
   * @returns An empty string to trigger the browser's confirmation dialog, or void if no changes
   */
  @HostListener('window:beforeunload', ['$event'])
  protected onBeforeUnload(event: BeforeUnloadEvent): string | void {
    // If we are currently ending an impersonation session (either local or via global state), bypass the unsaved changes warning
    if (this.impersonationService?.isEndingImpersonation() || (window as any).__symphonyIsEndingImpersonation) {
      return;
    }

    // Sign-out (manual or idle-timeout) is already tearing down the session — don't block the redirect with a native prompt.
    if (this.authState && SIGN_OUT_STATUSES.has(this.authState.getSession().status)) {
      return;
    }

    if (this.hasUnsavedChanges()) {
      // Calling preventDefault() triggers the browser's native confirmation dialog
      event.preventDefault();
      // Returning any value (even empty string) is required for some browsers
      return '';
    }
    return;
  }
}
